<script lang="ts">
	import type { Coordinates } from '$lib/features/_other/coordinates.type';
	import { Marker } from 'svelte-maplibre-gl';
	import IconFlame from '~icons/mdi/flame';

	type Alignment = 'map' | 'viewport' | 'auto';

	interface Props {
		wiki: {
			title: string;
			imageUrl: string;
			coords: Coordinates;
			isPopular: boolean;
		};
		shape: 'circle' | 'square';
		isLoading?: boolean;
		isActive: boolean;
		pitchAlignment?: Alignment;
		rotationAlignment?: Alignment;
		ariaLabel?: string;
		onclick?: () => void;
	}

	let {
		wiki,
		shape,
		isActive,
		isLoading = false,
		pitchAlignment = 'viewport',
		rotationAlignment = 'viewport',
		ariaLabel = `Inspect ${wiki.title}`,
		onclick
	}: Props = $props();
</script>

{#if wiki.imageUrl}
	<Marker
		lnglat={{ lng: wiki.coords.longitude, lat: wiki.coords.latitude }}
		{pitchAlignment}
		{rotationAlignment}
		onclick={() => onclick?.()}
	>
		{#snippet content()}
			<button
				class="group pointer-events-auto relative flex -translate-y-1/2 cursor-pointer flex-col items-center select-none"
				type="button"
				aria-label={ariaLabel}
			>
				{@render TitleBadge()}
				{@render PhotoPin()}
			</button>
		{/snippet}
	</Marker>
{/if}

{#snippet TitleBadge()}
	<div
		class={{
			'pointer-events-none relative z-20 inline-flex max-w-30 items-center rounded-field px-1.5 py-0.5 text-xs whitespace-nowrap transition-all duration-200 group-hover:scale-110 group-hover:opacity-100 sm:max-w-37.5': true,
			'-translate-y-2.5 scale-110': isActive,
			'border border-highlight bg-highlight-bg/90 text-highlight-content group-hover:border-highlight': wiki.isPopular,
			'border border-white/20 bg-base-300/90 text-base-content group-hover:border-primary': !wiki.isPopular
		}}
	>
		{#if wiki.isPopular}
			<IconFlame class="mr-1 text-xs text-highlight" />
		{/if}
		<span class="truncate">{wiki.title}</span>
	</div>
{/snippet}

{#snippet PhotoPin()}
	<div
		class={{
			'relative flex size-10 items-center justify-center overflow-hidden bg-accent text-accent-content shadow-xl transition-all duration-200 group-hover:scale-110 sm:size-11': true,
			'rounded-full': shape === 'circle',
			'rounded-field': shape === 'square',
			'z-10 scale-150 border-3 border-primary': isActive,
			'border-2 border-highlight ring-2 ring-highlight/50': !isActive && wiki.isPopular,
			'border-2 border-accent-content': !isActive && !wiki.isPopular
		}}
	>
		{#if wiki.imageUrl}
			<img class="size-full object-cover" src={wiki.imageUrl} alt={wiki.title} decoding="async" draggable="false" />
		{/if}

		{#if isLoading}
			<div class="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
				<span class="loading loading-sm loading-spinner"></span>
			</div>
		{/if}
	</div>
{/snippet}
